import React from 'react';
import { X, Filter } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getHODInfo } from '../../data/mockData';

const StudentProgressFilters = ({ 
  filters, 
  selectedFilters, 
  onFilterChange, 
  onClearFilters,
  allowedBranches,
  allowedYears
}) => {
  const { user } = useAuth();
  const hodInfo = getHODInfo(user?.email);
  const hasRestrictedBranches = allowedBranches && allowedBranches.length > 0;
  const hasRestrictedYears = allowedYears && allowedYears.length > 0;
  // Check for active filters (excluding HOD-restricted filters)
  const hasActiveFilters = 
    (!hasRestrictedYears && selectedFilters.year) || 
    (!hasRestrictedBranches && selectedFilters.branch) || 
    selectedFilters.domain || 
    selectedFilters.activity;

  return (
    <div className="filters-section">
      <div className="filters-header">
        <div className="filters-title">
          <Filter size={20} />
          <h3>Filters</h3>
          {hodInfo && (
            <span style={{ marginLeft: '10px', fontSize: '0.9em', color: '#666' }}>
              ({hodInfo.name})
            </span>
          )}
        </div>
        {hasActiveFilters && (
          <button 
            onClick={onClearFilters} 
            className="btn btn-secondary btn-sm clear-filters"
          >
            <X size={16} />
            Clear All
          </button>
        )}
      </div>

      <div className="filters-grid">
        {/* Year Filter */}
        <div className="filter-group">
          <label htmlFor="year-filter" className="filter-label">
            Year {allowedYears && <span style={{ fontSize: '0.85em', color: '#666' }}>(Your Years)</span>}
          </label>
          <select
            id="year-filter"
            value={selectedFilters.year || ''}
            onChange={(e) => onFilterChange('year', e.target.value || null)}
            className="filter-select"
          >
            {!allowedYears && <option value="">All Years</option>}
            {filters.years.map((year) => (
              <option key={year} value={year}>
                Year {year}
              </option>
            ))}
          </select>
        </div>

        {/* Branch Filter */}
        <div className="filter-group">
          <label htmlFor="branch-filter" className="filter-label">
            Branch {hasRestrictedBranches && <span style={{ fontSize: '0.85em', color: '#666' }}>(Your Branches)</span>}
          </label>
          <select
            id="branch-filter"
            value={selectedFilters.branch || ''}
            onChange={(e) => onFilterChange('branch', e.target.value || null)}
            className="filter-select"
          >
            {!hasRestrictedBranches && <option value="">All Branches</option>}
            {filters.branches.map((branch) => (
              <option key={branch} value={branch}>
                {branch}
              </option>
            ))}
          </select>
        </div>

        {/* Domain Filter */}
        <div className="filter-group">
          <label htmlFor="domain-filter" className="filter-label">
            Domain
          </label>
          <select
            id="domain-filter"
            value={selectedFilters.domain || ''}
            onChange={(e) => onFilterChange('domain', e.target.value || null)}
            className="filter-select"
          >
            <option value="">All Domains</option>
            {filters.domains.map((domain) => (
              <option key={domain} value={domain}>
                {domain}
              </option>
            ))}
          </select>
        </div>

        {/* Activity Filter */}
        <div className="filter-group">
          <label htmlFor="activity-filter" className="filter-label">
            Activity Status
          </label>
          <select
            id="activity-filter"
            value={selectedFilters.activity || ''}
            onChange={(e) => onFilterChange('activity', e.target.value || null)}
            className="filter-select"
          >
            <option value="">All Students</option>
            {filters.activity_options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="active-filters">
          <span className="active-filters-label">Active filters:</span>
          <div className="active-filters-tags">
            {selectedFilters.year && !hasRestrictedYears && (
              <div className="filter-tag">
                Year {selectedFilters.year}
                <button 
                  onClick={() => onFilterChange('year', null)}
                  className="filter-tag-remove"
                  aria-label="Remove year filter"
                >
                  <X size={14} />
                </button>
              </div>
            )}
            {selectedFilters.branch && !hasRestrictedBranches && (
              <div className="filter-tag">
                {selectedFilters.branch}
                <button 
                  onClick={() => onFilterChange('branch', null)}
                  className="filter-tag-remove"
                  aria-label="Remove branch filter"
                >
                  <X size={14} />
                </button>
              </div>
            )}
            {selectedFilters.domain && (
              <div className="filter-tag">
                {selectedFilters.domain}
                <button 
                  onClick={() => onFilterChange('domain', null)}
                  className="filter-tag-remove"
                  aria-label="Remove domain filter"
                >
                  <X size={14} />
                </button>
              </div>
            )}
            {selectedFilters.activity && (
              <div className="filter-tag">
                {filters.activity_options.find(opt => opt.value === selectedFilters.activity)?.label}
                <button 
                  onClick={() => onFilterChange('activity', null)}
                  className="filter-tag-remove"
                  aria-label="Remove activity filter"
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentProgressFilters;
